import React, { useEffect, useMemo, useState } from 'react';
import { Copy, Moon } from 'lucide-react';

const DIAS = [
  { clave: 'mon', corto: 'Lun', largo: 'Lunes' },
  { clave: 'tue', corto: 'Mar', largo: 'Martes' },
  { clave: 'wed', corto: 'Mié', largo: 'Miércoles' },
  { clave: 'thu', corto: 'Jue', largo: 'Jueves' },
  { clave: 'fri', corto: 'Vie', largo: 'Viernes' },
  { clave: 'sat', corto: 'Sáb', largo: 'Sábado' },
  { clave: 'sun', corto: 'Dom', largo: 'Domingo' }
];

// Intl devuelve el día en inglés; se traduce a las claves que usa el backend.
const CLAVE_POR_DIA_EN = { Mon: 'mon', Tue: 'tue', Wed: 'wed', Thu: 'thu', Fri: 'fri', Sat: 'sat', Sun: 'sun' };

const aMinutos = (hhmm) => {
  const [h, m] = String(hhmm || '').split(':').map(Number);
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
};

/** Qué hora y qué día es AHORA en la zona del negocio, no en la del navegador. */
const ahoraEnLaZona = (timezone) => {
  try {
    const partes = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone, weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false
    }).formatToParts(new Date());
    const de = (tipo) => partes.find(p => p.type === tipo)?.value;
    return { dia: CLAVE_POR_DIA_EN[de('weekday')] ?? null, minutos: Number(de('hour')) * 60 + Number(de('minute')) };
  } catch {
    return { dia: null, minutos: null };
  }
};

/** Los tramos del día que quedan cubiertos, en porcentaje, para dibujar la barra. */
const tramos = (inicio, fin) => {
  const i = aMinutos(inicio);
  const f = aMinutos(fin);
  if (i === null || f === null) return [];

  const pct = (min) => (min / 1440) * 100;

  if (i === f) return [{ left: 0, width: 100 }];            // todo el día
  if (i < f) return [{ left: pct(i), width: pct(f - i) }];  // franja normal
  // Cruza la medianoche: se dibuja partida en dos.
  return [
    { left: pct(i), width: pct(1440 - i) },
    { left: 0, width: pct(f) }
  ];
};

/**
 * Horario del piloto automático, día por día.
 *
 * La barra de 24 horas es lo que hace entendible la configuración: con siete
 * pares de horas sueltas nadie ve si quedó un hueco, y ese hueco es justo el
 * problema que esto vino a resolver (los domingos de Marmolgres, tres horas sin
 * vendedores y sin IA).
 */
const HorarioSemanal = ({ value, onChange, timezone = 'America/Caracas' }) => {
  const [ahora, setAhora] = useState(() => ahoraEnLaZona(timezone));
  // Un día abierto a la vez: siete pares de campos de hora no entran en la
  // columna lateral, y abiertos todos a la vez el bloque mide 900px de alto.
  const [diaAbierto, setDiaAbierto] = useState(null);

  // El indicador de "ahora" se mueve solo: si no, marca la hora en que se
  // abrió la pantalla y engaña.
  useEffect(() => {
    setAhora(ahoraEnLaZona(timezone));
    const id = setInterval(() => setAhora(ahoraEnLaZona(timezone)), 30000);
    return () => clearInterval(id);
  }, [timezone]);

  const dias = value || {};

  const cambiar = (clave, campo, valor) => {
    onChange({ ...dias, [clave]: { ...(dias[clave] || {}), [campo]: valor } });
  };

  const copiarATodos = (clave) => {
    const origen = dias[clave] || {};
    const copia = {};
    DIAS.forEach(d => { copia[d.clave] = { ...origen }; });
    onChange(copia);
  };

  // Si hoy el piloto responde o no, calculado con lo que hay en pantalla — no
  // con lo último guardado: al mover una hora el cambio se ve al instante.
  const activoAhora = useMemo(() => {
    if (!ahora.dia || ahora.minutos === null) return null;
    const hoy = dias[ahora.dia];
    if (!hoy || !hoy.activo) return false;
    const i = aMinutos(hoy.inicio);
    const f = aMinutos(hoy.fin);
    if (i === null || f === null) return null;
    if (i === f) return true;
    return i < f ? (ahora.minutos >= i && ahora.minutos < f) : (ahora.minutos >= i || ahora.minutos < f);
  }, [dias, ahora]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-[11px] text-slate-500 dark:text-gray-400">
          Hora local del negocio ({timezone})
        </p>
        {activoAhora !== null && (
          <span
            className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${activoAhora
              ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
              : 'bg-slate-500/10 text-slate-600 dark:text-gray-300'}`}
          >
            {activoAhora ? 'Ahora responde la IA' : 'Ahora está en modo asistido'}
          </span>
        )}
      </div>

      <div className="space-y-1">
        {DIAS.map(({ clave, corto, largo }) => {
          const dia = dias[clave] || { activo: false, inicio: '', fin: '' };
          const cruzaMedianoche = aMinutos(dia.inicio) !== null && aMinutos(dia.fin) !== null
            && aMinutos(dia.inicio) > aMinutos(dia.fin);
          const todoElDia = dia.activo && dia.inicio && dia.inicio === dia.fin;
          const esHoy = ahora.dia === clave;
          const abierto = diaAbierto === clave;

          return (
            <div
              key={clave}
              className={`rounded-xl border transition-colors ${esHoy
                ? 'border-primary/30 bg-primary/[0.04]'
                : abierto
                  ? 'border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/[0.03]'
                  : 'border-transparent'}`}
            >
              <div className="flex items-center gap-2 px-2.5 py-2">
                <label className="flex items-center gap-2 cursor-pointer select-none shrink-0">
                  <input
                    type="checkbox"
                    checked={!!dia.activo}
                    onChange={(e) => cambiar(clave, 'activo', e.target.checked)}
                    className="sr-only peer"
                    aria-label={`Piloto automático los ${largo}`}
                  />
                  <span className="w-8 h-[18px] shrink-0 bg-slate-200 dark:bg-white/10 rounded-full relative peer-checked:bg-primary peer-focus-visible:ring-2 peer-focus-visible:ring-primary/50 transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-3.5 after:w-3.5 after:transition-transform peer-checked:after:translate-x-[14px]"></span>
                  <span className={`text-xs font-bold w-7 ${dia.activo ? 'text-slate-800 dark:text-white' : 'text-slate-400 dark:text-gray-500'}`}>
                    {corto}
                  </span>
                </label>

                {/* La franja como texto: en 191px no entran dos campos de hora
                    junto al día. Se tocan para editarlas. */}
                <button
                  type="button"
                  onClick={() => setDiaAbierto(abierto ? null : clave)}
                  disabled={!dia.activo}
                  aria-expanded={abierto}
                  className={`flex-1 min-w-0 text-right text-[11px] font-bold tabular-nums whitespace-nowrap rounded-lg px-1.5 py-1 transition-colors ${dia.activo
                    ? 'text-slate-700 dark:text-gray-200 hover:bg-white dark:hover:bg-white/10'
                    : 'text-slate-400 dark:text-gray-500 cursor-default'}`}
                >
                  {dia.activo ? (
                    <>
                      {dia.inicio || '--:--'} – {dia.fin || '--:--'}
                      {cruzaMedianoche && <Moon size={10} className="inline ml-1 -mt-0.5 text-indigo-400" aria-label="Cruza la medianoche" />}
                    </>
                  ) : 'Sin piloto'}
                </button>
              </div>

              {/* Barra de 24 horas */}
              <div className="px-2.5 pb-2">
                <div className="relative h-1.5 rounded-full bg-slate-200/80 dark:bg-white/10 overflow-hidden">
                  {dia.activo && tramos(dia.inicio, dia.fin).map((t, i) => (
                    <span
                      key={i}
                      className="absolute inset-y-0 bg-primary/70 rounded-full"
                      style={{ left: `${t.left}%`, width: `${t.width}%` }}
                    />
                  ))}
                  {esHoy && ahora.minutos !== null && (
                    <span
                      className="absolute inset-y-0 w-0.5 bg-slate-900 dark:bg-white"
                      style={{ left: `${(ahora.minutos / 1440) * 100}%` }}
                      aria-hidden="true"
                    />
                  )}
                </div>
              </div>

              {abierto && dia.activo && (
                <div className="px-2.5 pb-2.5 space-y-2">
                  {/* Apilados y no lado a lado: el campo de hora nativo en
                      español muestra "05:10 p. m." y a 191px dos en una línea
                      quedan cortados a "05:1". */}
                  <div className="space-y-1.5">
                    <label className="block">
                      <span className="text-[10px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Desde</span>
                      <input
                        type="time"
                        value={dia.inicio || ''}
                        onChange={(e) => cambiar(clave, 'inicio', e.target.value)}
                        aria-label={`Desde, ${largo}`}
                        className="mt-0.5 w-full bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg px-2 py-1.5 text-xs font-bold text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/50 tabular-nums"
                      />
                    </label>
                    <label className="block">
                      <span className="text-[10px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Hasta</span>
                      <input
                        type="time"
                        value={dia.fin || ''}
                        onChange={(e) => cambiar(clave, 'fin', e.target.value)}
                        aria-label={`Hasta, ${largo}`}
                        className="mt-0.5 w-full bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg px-2 py-1.5 text-xs font-bold text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/50 tabular-nums"
                      />
                    </label>
                  </div>
                  <button
                    type="button"
                    onClick={() => copiarATodos(clave)}
                    className="w-full flex items-center justify-center gap-1.5 text-[11px] font-bold text-slate-500 dark:text-gray-400 hover:text-primary rounded-lg py-1.5 hover:bg-primary/10 transition-colors"
                  >
                    <Copy size={12} /> Aplicar a toda la semana
                  </button>
                  {todoElDia && (
                    <p className="text-[10px] text-amber-600 dark:text-amber-400 font-semibold">
                      Las dos horas son iguales: responderá todo el día.
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-[11px] text-slate-400 dark:text-gray-500 leading-relaxed">
        La barra muestra las horas en que la IA responde sola; la línea vertical es la hora actual.
        Un horario como 17:00 – 08:00 cruza la medianoche <Moon size={10} className="inline -mt-0.5 text-indigo-400" />, y la
        madrugada cuenta como parte del día en que ocurre.
      </p>
    </div>
  );
};

export default HorarioSemanal;
